import { Component, Input, OnInit } from '@angular/core'
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms'
import { Store } from '@ngrx/store'
import {
  BdcBathroom,
  BdcBathroomDetails,
  BdcBathroomGroup,
  BdcBathroomLocationType,
  BdcBathroomType,
  BdcContentV2Service,
  BdcFacilityState,
  BdcHotelBathroomDetails,
  BdcPrivateBathroomType,
  getBdcBathroomGroup,
} from '@tokeet-frontend/channel-content'
import { ActionFailed, compactObject, Toaster } from '@tokeet-frontend/tv3-platform'
import { combineLatest } from 'rxjs'
import { finalize } from 'rxjs/operators'

const maxBathrooms = 20

// Booking.com: 0 bathrooms when both states are MISSING, at least 1 per PRESENT state, at most 20.
function hotelBathroomCountValidator(form: AbstractControl): ValidationErrors | null {
  const { private_bathroom_state, shared_bathroom_state, number_of_bathrooms } = form.value
  const min = [private_bathroom_state, shared_bathroom_state].filter((s) => s === BdcFacilityState.PRESENT).length
  if (!min) {
    return null
  }
  const isValid =
    Number.isInteger(number_of_bathrooms) && number_of_bathrooms >= min && number_of_bathrooms <= maxBathrooms
  return isValid ? null : { bathroomCount: min }
}

@Component({
  selector: 'app-bdc-property-unit-bathroom',
  templateUrl: './bdc-property-unit-bathroom.component.html',
})
export class BdcPropertyUnitBathroomComponent implements OnInit {
  @Input() propertyId: number
  @Input() roomId: number
  @Input() propertyTypeId: number
  @Input() roomTypeId: number

  groups = BdcBathroomGroup
  facilityState = BdcFacilityState
  bathroomType = BdcBathroomType
  privateBathroomTypes = Object.values(BdcPrivateBathroomType)
  locationTypes = Object.values(BdcBathroomLocationType)
  locationOther = BdcBathroomLocationType.OTHER
  maxBathrooms = maxBathrooms

  group: BdcBathroomGroup | null
  form: FormGroup
  allowedLocations: Partial<Record<BdcBathroomType, string[]>> = {}
  isLoading = false

  get bathrooms() {
    return this.form.get('bathrooms') as FormArray
  }

  constructor(
    private fb: FormBuilder,
    private store: Store<any>,
    private toaster: Toaster,
    private contentService: BdcContentV2Service
  ) {}

  ngOnInit(): void {
    this.group = getBdcBathroomGroup(this.propertyTypeId)
    if (this.group === null) {
      return
    }
    this.isLoading = true
    combineLatest([
      this.contentService.getFacilitiesMeta(),
      this.contentService.getRoomBathrooms(this.propertyId, this.roomId),
    ])
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(
        ([meta, details]) => {
          const config = meta.bathroom_configurations.find((c) => c.unit_type_id === this.roomTypeId)
          config?.allowed_options.forEach((o) => (this.allowedLocations[o.bathroom_type] = o.bathroom_locations))
          this.buildForm(details)
        },
        (error) => {
          this.store.dispatch(ActionFailed({ error }))
        }
      )
  }

  buildForm(details: BdcBathroomDetails) {
    this.form =
      this.group === BdcBathroomGroup.Hotel
        ? this.hotelForm(details?.hotel_bathroom_details)
        : this.fb.group({
            bathrooms: this.fb.array(
              (details?.property_bathroom_details?.bathrooms || []).map((b) => this.bathroomForm(b))
            ),
          })
  }

  hotelForm(details?: BdcHotelBathroomDetails) {
    return this.fb.group(
      {
        private_bathroom_state: [details?.private_bathroom_state || BdcFacilityState.MISSING],
        shared_bathroom_state: [details?.shared_bathroom_state || BdcFacilityState.MISSING],
        number_of_bathrooms: [details?.number_of_bathrooms || 0],
        private_bathroom_details: this.fb.group({
          type: [details?.private_bathroom_details?.type],
          location_type: [details?.private_bathroom_details?.location_type],
          location_other: [details?.private_bathroom_details?.location_other],
        }),
      },
      { validators: hotelBathroomCountValidator }
    )
  }

  bathroomForm(bathroom?: BdcBathroom) {
    const form = this.fb.group(
      {
        id: [bathroom?.id],
        bathroom_type: [bathroom?.bathroom_type, [Validators.required]],
        bathroom_location: [bathroom?.bathroom_location],
      },
      { validators: this.allowedLocationValidator }
    )
    // the allowed locations depend on the bathroom type
    form.get('bathroom_type').valueChanges.subscribe(() => form.get('bathroom_location').reset())
    return form
  }

  // a loaded location can be one that the unit type no longer allows
  allowedLocationValidator = (form: AbstractControl): ValidationErrors | null => {
    const { bathroom_type, bathroom_location } = form.value
    const allowed = this.allowedLocations[bathroom_type] || []
    return !bathroom_location || allowed.includes(bathroom_location) ? null : { locationNotAllowed: true }
  }

  addBathroom() {
    this.bathrooms.push(this.bathroomForm())
  }

  getPayload(): BdcBathroomDetails {
    const data = this.form.getRawValue()
    if (this.group === BdcBathroomGroup.Hotel) {
      const hasPrivate = data.private_bathroom_state === BdcFacilityState.PRESENT
      const hasAny = hasPrivate || data.shared_bathroom_state === BdcFacilityState.PRESENT
      const details = data.private_bathroom_details
      return {
        hotel_bathroom_details: compactObject({
          ...data,
          number_of_bathrooms: hasAny ? data.number_of_bathrooms : 0,
          private_bathroom_details: hasPrivate
            ? {
                ...details,
                location_other: details.location_type === this.locationOther ? details.location_other : null,
              }
            : null,
        }),
      }
    }
    // overlay mode: the full list with the existing ids, a bathroom left out is deleted on Booking.com
    return { property_bathroom_details: { bathrooms: data.bathrooms.map((b) => compactObject(b)) } }
  }

  save() {
    if (!this.form || this.form.invalid) {
      return
    }
    const payload = this.getPayload()
    // block edits while saving: the response rebuilds the form
    // no events: the bathroom_type subscription would reset the locations
    this.form.disable({ emitEvent: false })
    this.isLoading = true
    this.contentService
      .updateRoomBathrooms(this.propertyId, this.roomId, payload)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(
        (details) => {
          // re-enable first: the Hotel form keeps its control directives, and a new control does not re-enable them
          this.form.enable({ emitEvent: false })
          // the response is the new configuration, with the ids of the added bathrooms
          this.buildForm(details)
          this.toaster.success($localize`Room bathrooms updated successfully`)
        },
        (error) => {
          this.form.enable({ emitEvent: false })
          this.store.dispatch(ActionFailed({ error }))
        }
      )
  }
}
